import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { PermissionService } from 'src/app/services/permission.service';
import { portalCommands } from 'src/app/utils/portal-path';
import { TranslateService } from '@ngx-translate/core';
import {
  AttendanceDayMark,
  AttendanceDayStatus,
  AttendanceRangeReport,
  AttendanceRangeRow,
  AttendanceReport,
  AttendanceReportRow,
  AttendanceService,
  AttendanceStatus,
} from 'src/app/services/attendance.service';
import {
  AttendancePdfClassReport,
  AttendancePdfService,
  AttendanceRangePdfClassReport,
} from 'src/app/services/attendance-pdf.service';
import { ClassesService } from 'src/app/services/classes.service';
import { LanguageService } from 'src/app/services/language.service';
import { SchoolSettingsService } from 'src/app/services/school-settings.service';
import { Week, WeeksService } from 'src/app/services/weeks.service';
import { SearchableSelectOption } from 'src/app/shared/searchable-select/searchable-select.component';

type StatusFilter = AttendanceDayStatus | 'all' | 'present' | 'check_in_late';
type PeriodMode = 'day' | 'week' | 'custom';

@Component({
  selector: 'app-attendance-dashboard',
  templateUrl: './attendance-dashboard.component.html',
  styleUrls: ['./attendance-dashboard.component.css']
})
export class AttendanceDashboardComponent implements OnInit {
  fromDate = '';
  toDate = '';
  selectedClassId = '';
  selectedWeekId = '';
  periodMode: PeriodMode = 'day';
  classes: any[] = [];
  weeks: Week[] = [];
  loading = true;
  exporting = false;
  report: AttendanceReport | null = null;
  rangeReport: AttendanceRangeReport | null = null;
  errorMessage = '';
  statusFilter: StatusFilter = 'all';
  search = '';
  private schoolName = '';
  private headName = '';
  private sectionHeadName = '';

  constructor(
    private attendanceService: AttendanceService,
    private attendancePdfService: AttendancePdfService,
    private classesService: ClassesService,
    private weeksService: WeeksService,
    private schoolSettingsService: SchoolSettingsService,
    private languageService: LanguageService,
    private translate: TranslateService,
    private router: Router,
    public permissions: PermissionService,
  ) {}

  ngOnInit(): void {
    const today = this.todayInputValue();
    this.fromDate = today;
    this.toDate = today;
    void this.init();
  }

  get isRangeMode(): boolean {
    return !!this.fromDate && !!this.toDate && this.fromDate !== this.toDate;
  }

  get lateEnabled(): boolean {
    const schedule = this.isRangeMode
      ? this.rangeReport?.schedule
      : this.report?.schedule;
    return schedule?.lateEnabled !== false;
  }

  get searchPlaceholder(): string {
    return this.translate.instant('COMMON.SEARCH');
  }

  get allClassesLabel(): string {
    return this.translate.instant('ATTENDANCE_DASH.ALL_CLASSES');
  }

  get selectWeekLabel(): string {
    return this.translate.instant('ATTENDANCE_DASH.SELECT_WEEK');
  }

  get classOptions(): SearchableSelectOption[] {
    return this.classes.map(cls => ({
      value: String(cls.id || cls._id || ''),
      label: cls.major?.name ? `${cls.name} — ${cls.major.name}` : (cls.name || ''),
      searchText: `${cls.name || ''} ${cls.major?.name || ''}`,
    })).filter(option => option.value);
  }

  get weekOptions(): SearchableSelectOption[] {
    return this.weeks.map(week => ({
      value: week.id,
      label: this.weekLabel(week),
      searchText: `${week.name || ''} ${week.ordinal || ''} ${week.startDate || ''} ${week.endDate || ''}`,
    })).filter(option => option.value);
  }

  get periodSummary(): string {
    if (!this.fromDate || !this.toDate) {
      return '';
    }
    if (this.fromDate === this.toDate) {
      return this.formatDayLong(this.fromDate);
    }
    const week = this.weeks.find(item => item.id === this.selectedWeekId);
    const arrow = this.languageService.current === 'ar' ? '←' : '→';
    const range = `${this.formatDayLong(this.fromDate)} ${arrow} ${this.formatDayLong(this.toDate)}`;
    if (week) {
      const name = week.name || `${this.translate.instant('WEEKS.WEEK')} ${week.ordinal}`;
      return `${name} · ${range}`;
    }
    return range;
  }

  get filteredRows(): AttendanceReportRow[] {
    const rows = this.report?.rows || [];
    const q = this.search.trim().toLowerCase();
    return rows.filter(row => {
      if (this.statusFilter === 'present' && row.dayStatus === 'absent') {
        return false;
      } else if (this.statusFilter === 'check_in_late' && row.checkIn?.status !== 'late') {
        return false;
      } else if (
        this.statusFilter !== 'all'
        && this.statusFilter !== 'present'
        && this.statusFilter !== 'check_in_late'
        && row.dayStatus !== this.statusFilter
      ) {
        return false;
      }
      if (!q) {
        return true;
      }
      return [
        row.studentNameAr,
        row.studentNameEn,
        row.studentCode,
        row.className,
      ].join(' ').toLowerCase().includes(q);
    });
  }

  get filteredRangeRows(): AttendanceRangeRow[] {
    const rows = this.rangeReport?.rows || [];
    const q = this.search.trim().toLowerCase();
    if (!q) {
      return rows;
    }
    return rows.filter(row => [
      row.studentNameAr,
      row.studentNameEn,
      row.studentCode,
      row.className,
    ].join(' ').toLowerCase().includes(q));
  }

  get attendanceRate(): number {
    if (this.isRangeMode) {
      return this.rangeReport?.summary?.attendanceRate || 0;
    }
    const total = this.report?.summary?.total || 0;
    const present = this.report?.summary?.present || 0;
    return total ? Math.round((present / total) * 100) : 0;
  }

  get absenceRate(): number {
    if (this.isRangeMode) {
      return this.rangeReport?.summary?.absenceRate || 0;
    }
    const total = this.report?.summary?.total || 0;
    const absent = this.report?.summary?.absent || 0;
    return total ? Math.round((absent / total) * 100) : 0;
  }

  get showDayColumns(): boolean {
    const days = this.rangeReport?.days?.length || 0;
    return days > 0 && days <= 10;
  }

  studentName(row: AttendanceReportRow | AttendanceRangeRow): string {
    const isAr = this.languageService.current === 'ar';
    return (isAr ? row.studentNameAr : row.studentNameEn) || row.studentNameAr || row.studentNameEn || '—';
  }

  dayStatusKey(status: AttendanceDayStatus): string {
    return `ATTENDANCE_DASH.DAY_${status.toUpperCase()}`;
  }

  timingKey(status?: AttendanceStatus | null): string {
    const normalized = (!this.lateEnabled && status === 'late') ? 'on_time' : (status || 'on_time');
    return `ATTENDANCE.STATUS_${normalized.toUpperCase()}`;
  }

  timingStatus(status?: AttendanceStatus | null): AttendanceStatus {
    if (!this.lateEnabled && status === 'late') {
      return 'on_time';
    }
    return status || 'on_time';
  }

  dayMarkAttr(mark?: AttendanceDayMark, day?: string): AttendanceDayMark | 'holiday' {
    if ((day && this.isHolidayDay(day)) || mark === 'holiday') {
      return 'holiday';
    }
    if (!this.lateEnabled && mark === 'late') {
      return 'present';
    }
    return mark || 'absent';
  }

  formatTime(at?: number | null): string {
    if (!at) {
      return '—';
    }
    return new Date(at).toLocaleTimeString(this.languageService.current === 'ar' ? 'ar-AE' : 'en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }

  formatDayShort(date: string): string {
    const value = new Date(`${date}T12:00:00`);
    if (Number.isNaN(value.getTime())) {
      return date.slice(5);
    }
    return value.toLocaleDateString(this.languageService.current === 'ar' ? 'ar-EG' : 'en-GB', {
      day: '2-digit',
      month: '2-digit',
    });
  }

  formatDayLong(date: string): string {
    const value = new Date(`${date}T12:00:00`);
    if (Number.isNaN(value.getTime())) {
      return date;
    }
    return value.toLocaleDateString(this.languageService.current === 'ar' ? 'ar-EG' : 'en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  markLetter(mark?: AttendanceDayMark): string {
    if (mark === 'holiday') {
      return '';
    }
    if (mark === 'late' && this.lateEnabled) {
      return this.translate.instant('ATTENDANCE_DASH.MARK_LATE');
    }
    if (mark === 'present' || mark === 'late') {
      return this.translate.instant('ATTENDANCE_DASH.MARK_PRESENT');
    }
    return this.translate.instant('ATTENDANCE_DASH.MARK_ABSENT');
  }

  rowAbsenceRate(row: AttendanceRangeRow): number {
    return Math.max(0, Math.min(100, 100 - (row.attendanceRate || 0)));
  }

  isHolidayDay(day: string): boolean {
    const schedule = this.rangeReport?.schedule;
    if (!schedule) {
      return false;
    }
    if (Array.isArray(schedule.holidayDates) && schedule.holidayDates.some(item => item?.date === day)) {
      return true;
    }
    const holidays = schedule.holidayWeekdays;
    if (!Array.isArray(holidays) || !holidays.length) {
      return false;
    }
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
    if (!match) {
      return false;
    }
    const weekday = new Date(`${match[1]}-${match[2]}-${match[3]}T08:00:00Z`).getUTCDay();
    return holidays.includes(weekday);
  }

  setFilter(status: StatusFilter): void {
    if (status === 'check_in_late' && !this.lateEnabled) {
      this.statusFilter = 'all';
      return;
    }
    this.statusFilter = status;
  }

  async setPeriodMode(mode: PeriodMode): Promise<void> {
    this.periodMode = mode;
    this.statusFilter = 'all';

    if (mode === 'day') {
      if (!this.fromDate || this.fromDate !== this.toDate) {
        const today = this.todayInputValue();
        this.fromDate = today;
        this.toDate = today;
      } else {
        this.toDate = this.fromDate;
      }
      this.selectedWeekId = '';
      await this.loadReport();
      return;
    }

    if (mode === 'week') {
      const preferred = this.weeks.find(week => week.id === this.selectedWeekId)
        || this.weeks.find(week => week.active)
        || this.weeks.find(week => week.startDate && week.endDate && week.startDate <= this.todayInputValue() && week.endDate >= this.todayInputValue())
        || this.weeks[this.weeks.length - 1];
      if (preferred?.id) {
        this.selectedWeekId = preferred.id;
        await this.onWeekChange();
      }
      return;
    }

    // custom: keep current dates; show the date pickers and stay in custom mode
    this.selectedWeekId = '';
    await this.loadReport();
  }

  async onDayChange(): Promise<void> {
    if (!this.fromDate) {
      this.fromDate = this.todayInputValue();
    }
    this.toDate = this.fromDate;
    this.periodMode = 'day';
    this.selectedWeekId = '';
    this.statusFilter = 'all';
    await this.loadReport();
  }

  async onDateWindowChange(): Promise<void> {
    if (this.fromDate && this.toDate && this.fromDate > this.toDate) {
      this.toDate = this.fromDate;
    }
    this.periodMode = 'custom';
    this.selectedWeekId = '';
    this.statusFilter = 'all';
    await this.loadReport();
  }

  async onClassChange(): Promise<void> {
    this.statusFilter = 'all';
    await this.loadReport();
  }

  async onWeekChange(): Promise<void> {
    const week = this.weeks.find(item => item.id === this.selectedWeekId);
    if (!week?.startDate || !week?.endDate) {
      return;
    }
    this.periodMode = 'week';
    this.fromDate = week.startDate;
    this.toDate = week.endDate;
    this.statusFilter = 'all';
    await this.loadReport();
  }

  weekLabel(week: Week): string {
    const name = week.name || `${this.translate.instant('WEEKS.WEEK')} ${week.ordinal}`;
    const active = week.active ? ` · ${this.translate.instant('WEEKS.ACTIVE')}` : '';
    return `${name}${active}`;
  }

  private syncSelectedWeekFromDates(): void {
    if (!this.fromDate || !this.toDate) {
      this.selectedWeekId = '';
      return;
    }
    const match = this.weeks.find(
      week => week.startDate === this.fromDate && week.endDate === this.toDate
    );
    this.selectedWeekId = match?.id || '';
  }

  async loadReport(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';
    try {
      if (this.isRangeMode) {
        this.report = null;
        this.rangeReport = await this.attendanceService.getRangeReport(
          this.fromDate,
          this.toDate,
          this.selectedClassId || undefined,
        );
        if (this.rangeReport?.from) {
          this.fromDate = this.rangeReport.from;
        }
        if (this.rangeReport?.to) {
          this.toDate = this.rangeReport.to;
        }
      } else {
        this.rangeReport = null;
        const date = this.fromDate || this.toDate || this.todayInputValue();
        this.fromDate = date;
        this.toDate = date;
        this.report = await this.attendanceService.getReport(
          date,
          this.selectedClassId || undefined,
        );
        if (this.report?.date) {
          this.fromDate = this.report.date;
          this.toDate = this.report.date;
        }
      }
    } catch (error: any) {
      this.report = null;
      this.rangeReport = null;
      this.errorMessage = error?.error?.errorMessages?.[0]?.message || 'ATTENDANCE_DASH.LOAD_FAILED';
    } finally {
      this.loading = false;
      if (!this.lateEnabled && this.statusFilter === 'check_in_late') {
        this.statusFilter = 'all';
      }
    }
  }

  async printPdf(): Promise<void> {
    if (this.exporting) {
      return;
    }
    if (!this.selectedClassId) {
      this.errorMessage = 'ATTENDANCE_DASH.PDF_SELECT_CLASS';
      return;
    }
    if (this.isRangeMode) {
      await this.printRangePdf();
      return;
    }
    await this.printDailyPdf();
  }

  async printAbsentPdf(): Promise<void> {
    if (this.exporting) {
      return;
    }
    if (this.isRangeMode) {
      await this.printRangeAbsentPdf();
      return;
    }
    await this.printDailyAbsentPdf();
  }

  get hasAbsentForPdf(): boolean {
    if (this.isRangeMode) {
      return (this.rangeReport?.rows || []).some(row => (row.absentDays || 0) > 0);
    }
    return (this.report?.summary?.absent || 0) > 0;
  }

  private async printDailyPdf(): Promise<void> {
    if (!this.report?.rows?.length) {
      return;
    }

    this.exporting = true;
    this.errorMessage = '';
    try {
      const classReports = this.buildClassReports(this.report.rows);
      if (!classReports.length) {
        this.errorMessage = 'ATTENDANCE_DASH.PDF_EMPTY';
        return;
      }

      await this.attendancePdfService.export({
        date: this.report.date || this.fromDate,
        schoolName: this.schoolName,
        headName: this.headName,
        sectionHeadName: this.sectionHeadName,
        isArabic: this.languageService.current === 'ar',
        lateEnabled: this.lateEnabled,
        labels: {
          title: this.translate.instant('ATTENDANCE_DASH.PDF_TITLE'),
          date: this.translate.instant('ATTENDANCE_DASH.DATE'),
          classLabel: this.translate.instant('STUDENTS.CLASS'),
          total: this.translate.instant('ATTENDANCE_DASH.TOTAL'),
          attended: this.translate.instant('ATTENDANCE_DASH.PDF_ATTENDED'),
          late: this.translate.instant('ATTENDANCE_DASH.PDF_LATE'),
          absent: this.translate.instant('ATTENDANCE_DASH.PDF_ABSENT'),
          attendanceRate: this.translate.instant('ATTENDANCE_DASH.ATTENDANCE_RATE'),
          absenceRate: this.translate.instant('ATTENDANCE_DASH.ABSENCE_RATE'),
          name: this.translate.instant('STUDENTS.NAME'),
          checkIn: this.translate.instant('ATTENDANCE.IN'),
          checkOut: this.translate.instant('ATTENDANCE.OUT'),
          timing: this.translate.instant('ATTENDANCE_DASH.PDF_TIMING'),
          emptySection: this.translate.instant('ATTENDANCE_DASH.PDF_EMPTY_SECTION'),
          noClass: this.translate.instant('ATTENDANCE_DASH.PDF_NO_CLASS'),
          statusEarly: this.translate.instant('ATTENDANCE.STATUS_EARLY'),
          statusOnTime: this.translate.instant('ATTENDANCE.STATUS_ON_TIME'),
          statusLate: this.translate.instant('ATTENDANCE.STATUS_LATE'),
        },
        classes: classReports,
      });
    } catch {
      this.errorMessage = 'ATTENDANCE_DASH.PDF_FAILED';
    } finally {
      this.exporting = false;
    }
  }

  private async printDailyAbsentPdf(): Promise<void> {
    if (!this.report?.rows?.length) {
      return;
    }

    this.exporting = true;
    this.errorMessage = '';
    try {
      const allClasses = !this.selectedClassId;
      // Only absent students — skip attended rows for a fast combined report.
      const absentRows = this.report.rows.filter(row => row.dayStatus === 'absent');
      if (!absentRows.length) {
        this.errorMessage = 'ATTENDANCE_DASH.PDF_ABSENT_EMPTY';
        return;
      }

      const classReports = this.buildClassReports(this.report.rows)
        .filter(item => item.absent.length > 0)
        .map(item => ({
          classId: item.classId,
          className: item.className,
          total: item.total,
          attended: [],
          late: [],
          absent: item.absent,
        }));
      if (!classReports.length) {
        this.errorMessage = 'ATTENDANCE_DASH.PDF_ABSENT_EMPTY';
        return;
      }

      await this.attendancePdfService.export({
        date: this.report.date || this.fromDate,
        schoolName: this.schoolName,
        headName: this.headName,
        sectionHeadName: this.sectionHeadName,
        isArabic: this.languageService.current === 'ar',
        lateEnabled: this.lateEnabled,
        absentOnly: true,
        combined: allClasses,
        labels: {
          title: this.translate.instant(
            allClasses
              ? 'ATTENDANCE_DASH.PDF_ABSENT_ALL_TITLE'
              : 'ATTENDANCE_DASH.PDF_ABSENT_TITLE'
          ),
          date: this.translate.instant('ATTENDANCE_DASH.DATE'),
          classLabel: this.translate.instant('STUDENTS.CLASS'),
          total: this.translate.instant('ATTENDANCE_DASH.TOTAL'),
          attended: this.translate.instant('ATTENDANCE_DASH.PDF_ATTENDED'),
          late: this.translate.instant('ATTENDANCE_DASH.PDF_LATE'),
          absent: this.translate.instant('ATTENDANCE_DASH.PDF_ABSENT'),
          attendanceRate: this.translate.instant('ATTENDANCE_DASH.ATTENDANCE_RATE'),
          absenceRate: this.translate.instant('ATTENDANCE_DASH.ABSENCE_RATE'),
          name: this.translate.instant('STUDENTS.NAME'),
          checkIn: this.translate.instant('ATTENDANCE.IN'),
          checkOut: this.translate.instant('ATTENDANCE.OUT'),
          timing: this.translate.instant('ATTENDANCE_DASH.PDF_TIMING'),
          emptySection: this.translate.instant('ATTENDANCE_DASH.PDF_EMPTY_SECTION'),
          noClass: this.translate.instant('ATTENDANCE_DASH.PDF_NO_CLASS'),
          allClasses: this.translate.instant('ATTENDANCE_DASH.ALL_CLASSES'),
          classesWithAbsent: this.translate.instant('ATTENDANCE_DASH.PDF_CLASSES_WITH_ABSENT'),
          statusEarly: this.translate.instant('ATTENDANCE.STATUS_EARLY'),
          statusOnTime: this.translate.instant('ATTENDANCE.STATUS_ON_TIME'),
          statusLate: this.translate.instant('ATTENDANCE.STATUS_LATE'),
        },
        classes: classReports,
      });
    } catch {
      this.errorMessage = 'ATTENDANCE_DASH.PDF_FAILED';
    } finally {
      this.exporting = false;
    }
  }

  private async printRangePdf(): Promise<void> {
    if (!this.rangeReport?.rows?.length) {
      return;
    }

    this.exporting = true;
    this.errorMessage = '';
    try {
      const classReports = this.buildRangeClassReports(this.rangeReport.rows);
      if (!classReports.length) {
        this.errorMessage = 'ATTENDANCE_DASH.PDF_EMPTY';
        return;
      }

      await this.attendancePdfService.exportRange({
        from: this.rangeReport.from,
        to: this.rangeReport.to,
        days: this.rangeReport.days,
        holidayWeekdays: this.rangeReport.schedule?.holidayWeekdays || [],
        holidayDates: (this.rangeReport.schedule?.holidayDates || []).map(item => item.date),
        lateEnabled: this.lateEnabled,
        schoolName: this.schoolName,
        headName: this.headName,
        sectionHeadName: this.sectionHeadName,
        isArabic: this.languageService.current === 'ar',
        summary: {
          totalStudents: this.rangeReport.summary.totalStudents,
          dayCount: this.rangeReport.summary.dayCount,
          attendanceRate: this.rangeReport.summary.attendanceRate,
          absenceRate: this.rangeReport.summary.absenceRate,
          late: this.rangeReport.summary.late,
        },
        labels: {
          title: this.translate.instant('ATTENDANCE_DASH.PDF_RANGE_TITLE'),
          dateRange: this.translate.instant('ATTENDANCE_DASH.DATE_RANGE'),
          classLabel: this.translate.instant('STUDENTS.CLASS'),
          totalStudents: this.translate.instant('ATTENDANCE_DASH.TOTAL'),
          dayCount: this.translate.instant('ATTENDANCE_DASH.DAY_COUNT'),
          attended: this.translate.instant('ATTENDANCE_DASH.PDF_ATTENDED'),
          late: this.translate.instant('ATTENDANCE_DASH.PDF_LATE'),
          absent: this.translate.instant('ATTENDANCE_DASH.PDF_ABSENT'),
          attendanceRate: this.translate.instant('ATTENDANCE_DASH.ATTENDANCE_RATE'),
          absenceRate: this.translate.instant('ATTENDANCE_DASH.ABSENCE_RATE'),
          name: this.translate.instant('STUDENTS.NAME'),
          presentDays: this.translate.instant('ATTENDANCE_DASH.PRESENT_DAYS'),
          absentDays: this.translate.instant('ATTENDANCE_DASH.ABSENT_DAYS'),
          lateDays: this.translate.instant('ATTENDANCE_DASH.LATE_DAYS'),
          rate: this.translate.instant('ATTENDANCE_DASH.ATTENDANCE_RATE'),
          emptySection: this.translate.instant('ATTENDANCE_DASH.PDF_EMPTY_SECTION'),
          noClass: this.translate.instant('ATTENDANCE_DASH.PDF_NO_CLASS'),
          markPresent: this.translate.instant('ATTENDANCE_DASH.MARK_PRESENT'),
          markAbsent: this.translate.instant('ATTENDANCE_DASH.MARK_ABSENT'),
          markLate: this.translate.instant('ATTENDANCE_DASH.MARK_LATE'),
        },
        classes: classReports,
      });
    } catch {
      this.errorMessage = 'ATTENDANCE_DASH.PDF_FAILED';
    } finally {
      this.exporting = false;
    }
  }

  private async printRangeAbsentPdf(): Promise<void> {
    if (!this.rangeReport?.rows?.length) {
      return;
    }

    this.exporting = true;
    this.errorMessage = '';
    try {
      const absentRows = this.rangeReport.rows.filter(row => (row.absentDays || 0) > 0);
      const classReports = this.buildRangeClassReports(absentRows)
        .filter(item => item.rows.length > 0);
      if (!classReports.length) {
        this.errorMessage = 'ATTENDANCE_DASH.PDF_ABSENT_EMPTY';
        return;
      }

      await this.attendancePdfService.exportRange({
        from: this.rangeReport.from,
        to: this.rangeReport.to,
        days: this.rangeReport.days,
        holidayWeekdays: this.rangeReport.schedule?.holidayWeekdays || [],
        holidayDates: (this.rangeReport.schedule?.holidayDates || []).map(item => item.date),
        lateEnabled: this.lateEnabled,
        schoolName: this.schoolName,
        headName: this.headName,
        sectionHeadName: this.sectionHeadName,
        isArabic: this.languageService.current === 'ar',
        summary: {
          totalStudents: absentRows.length,
          dayCount: this.rangeReport.summary.dayCount,
          attendanceRate: this.rangeReport.summary.attendanceRate,
          absenceRate: this.rangeReport.summary.absenceRate,
          late: this.rangeReport.summary.late,
        },
        labels: {
          title: this.translate.instant('ATTENDANCE_DASH.PDF_ABSENT_RANGE_TITLE'),
          dateRange: this.translate.instant('ATTENDANCE_DASH.DATE_RANGE'),
          classLabel: this.translate.instant('STUDENTS.CLASS'),
          totalStudents: this.translate.instant('ATTENDANCE_DASH.TOTAL'),
          dayCount: this.translate.instant('ATTENDANCE_DASH.DAY_COUNT'),
          attended: this.translate.instant('ATTENDANCE_DASH.PDF_ATTENDED'),
          late: this.translate.instant('ATTENDANCE_DASH.PDF_LATE'),
          absent: this.translate.instant('ATTENDANCE_DASH.PDF_ABSENT'),
          attendanceRate: this.translate.instant('ATTENDANCE_DASH.ATTENDANCE_RATE'),
          absenceRate: this.translate.instant('ATTENDANCE_DASH.ABSENCE_RATE'),
          name: this.translate.instant('STUDENTS.NAME'),
          presentDays: this.translate.instant('ATTENDANCE_DASH.PRESENT_DAYS'),
          absentDays: this.translate.instant('ATTENDANCE_DASH.ABSENT_DAYS'),
          lateDays: this.translate.instant('ATTENDANCE_DASH.LATE_DAYS'),
          rate: this.translate.instant('ATTENDANCE_DASH.ATTENDANCE_RATE'),
          emptySection: this.translate.instant('ATTENDANCE_DASH.PDF_EMPTY_SECTION'),
          noClass: this.translate.instant('ATTENDANCE_DASH.PDF_NO_CLASS'),
          markPresent: this.translate.instant('ATTENDANCE_DASH.MARK_PRESENT'),
          markAbsent: this.translate.instant('ATTENDANCE_DASH.MARK_ABSENT'),
          markLate: this.translate.instant('ATTENDANCE_DASH.MARK_LATE'),
        },
        classes: classReports,
      });
    } catch {
      this.errorMessage = 'ATTENDANCE_DASH.PDF_FAILED';
    } finally {
      this.exporting = false;
    }
  }

  openStudent(row: AttendanceReportRow | AttendanceRangeRow): void {
    if (!row.studentId || !this.permissions.canView('students')) {
      return;
    }
    void this.router.navigate(portalCommands(this.router, 'settings', 'students', row.studentId));
  }

  openScanner(): void {
    void this.router.navigate(portalCommands(this.router, 'attendance'));
  }

  private buildClassReports(rows: AttendanceReportRow[]): AttendancePdfClassReport[] {
    return this.orderedClassIds(rows).map(id => {
      const classRows = rows.filter(row => (row.classId || '__none__') === id);
      return {
        classId: id,
        className: this.classNameFor(id, classRows[0]?.className),
        total: classRows.length,
        attended: classRows.filter(row => row.dayStatus !== 'absent'),
        late: classRows.filter(row => row.checkIn?.status === 'late'),
        absent: classRows.filter(row => row.dayStatus === 'absent'),
      };
    });
  }

  private buildRangeClassReports(rows: AttendanceRangeRow[]): AttendanceRangePdfClassReport[] {
    return this.orderedClassIds(rows).map(id => {
      const classRows = rows.filter(row => (row.classId || '__none__') === id);
      return {
        classId: id,
        className: this.classNameFor(id, classRows[0]?.className),
        rows: classRows.map(row => ({
          studentName: this.studentName(row),
          presentDays: row.presentDays,
          absentDays: row.absentDays,
          lateDays: row.lateDays,
          attendanceRate: row.attendanceRate,
          dayMarks: row.dayMarks,
        })),
      };
    });
  }

  private orderedClassIds(rows: Array<{ classId: string }>): string[] {
    const byClass = new Set(rows.map(row => row.classId || '__none__'));
    const knownIds = this.classes
      .map(cls => String(cls.id || cls._id || ''))
      .filter(id => id && byClass.has(id));
    const extraIds = [...byClass].filter(id => id !== '__none__' && !knownIds.includes(id));

    if (this.selectedClassId) {
      return byClass.has(this.selectedClassId) ? [this.selectedClassId] : [];
    }
    return [
      ...knownIds,
      ...extraIds,
      ...(byClass.has('__none__') ? ['__none__'] : []),
    ];
  }

  private classNameFor(id: string, fallback?: string): string {
    if (id === '__none__') {
      return this.translate.instant('ATTENDANCE_DASH.PDF_NO_CLASS');
    }
    return fallback
      || this.classes.find(cls => String(cls.id || cls._id) === id)?.name
      || id;
  }

  private async init(): Promise<void> {
    try {
      const [classes, weeks, settings] = await Promise.all([
        this.classesService.list(),
        this.weeksService.list(),
        this.schoolSettingsService.get().catch(() => null),
      ]);
      this.classes = (classes || [])
        .slice()
        .sort((a: any, b: any) => String(a.name || '').localeCompare(String(b.name || ''), 'ar'));
      this.weeks = (weeks || [])
        .slice()
        .sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0));
      this.schoolName = settings?.schoolName || '';
      this.headName = settings?.headName || '';
      this.sectionHeadName = (settings?.sectionHeads || [])
        .map(item => item.name?.trim())
        .find(Boolean) || '';
      this.syncSelectedWeekFromDates();
    } catch {
      this.classes = [];
      this.weeks = [];
    }
    await this.loadReport();
  }

  private todayInputValue(): string {
    const now = new Date();
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 10);
  }
}
